import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  ButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { getProjects, getProjectTimeline, deleteProjectTimeline } from '../services/api';
import { formatDateEuropean } from '../utils/dateUtils';
import TimelineWizard from '../components/ProjectTimeline/TimelineWizard';
import GanttChart from '../components/ProjectTimeline/GanttChart';
import { useTranslation } from 'react-i18next';

const ProjectTimelines = () => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [timelineViewMode, setTimelineViewMode] = useState('general'); // 'general' | 'detailed'
  const timelineScrollContainerRef = useRef(null);

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      loadTimeline();
    } else {
      setTimeline(null);
    }
  }, [selectedProject]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await getProjects();
      setProjects(response.data);
    } catch (error) {
      console.error('Error loading projects:', error);
      setError(t('projectTimelines.failedLoadProjects'));
    } finally {
      setLoading(false);
    }
  };

  const loadTimeline = async () => {
    if (!selectedProject) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await getProjectTimeline(selectedProject);
      setTimeline(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setTimeline(null); // No timeline exists
      } else {
        console.error('Error loading timeline:', error);
        setError(t('projectTimelines.failedLoadTimeline'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (event) => {
    setSelectedProject(event.target.value);
  };

  const handleCreateTimeline = () => {
    setWizardOpen(true);
  };

  const handleEditTimeline = () => {
    setWizardOpen(true);
  };

  const handleDeleteTimeline = async () => {
    try {
      setLoading(true);
      await deleteProjectTimeline(timeline.id);
      setTimeline(null);
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting timeline:', error);
      setError(t('projectTimelines.failedDeleteTimeline'));
    } finally {
      setLoading(false);
    }
  };

  const handleWizardClose = () => {
    setWizardOpen(false);
    if (selectedProject) {
      loadTimeline(); // Refresh timeline data
    }
  };

  const handleRefresh = () => {
    if (selectedProject) {
      loadTimeline();
    }
  };

  const selectedProjectData = projects.find(p => p.id === parseInt(selectedProject));

  return (
    <Box sx={{ p: 3, width: '100%', minWidth: 0, maxWidth: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{t('projectTimelines.title')}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t('common.refresh')}>
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Project Selector */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>{t('projectTimelines.selectProject')}</InputLabel>
          <Select
            value={selectedProject}
            onChange={handleProjectChange}
            label={t('projectTimelines.selectProject')}
            disabled={loading}
          >
            {projects.map((project) => (
              <MenuItem key={project.id} value={project.id}>
                {project.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* No Project Selected */}
      {!selectedProject && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <TimelineIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {t('projectTimelines.noProjectSelectedTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('projectTimelines.noProjectSelectedBody')}
          </Typography>
        </Paper>
      )}

      {/* No Timeline Exists */}
      {selectedProject && !timeline && !loading && !error && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <TimelineIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('projectTimelines.noTimelineTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('projectTimelines.noTimelineBody')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateTimeline}
            size="large"
          >
            {t('projectTimelines.createTimeline')}
          </Button>
        </Paper>
      )}

      {/* Timeline Exists - Show Gantt Chart */}
      {selectedProject && timeline && !loading && (
        <Box sx={{ width: '100%', minWidth: 0 }}>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  {selectedProjectData?.name} - {t('projectTimelines.projectTimeline')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('projectTimelines.created')}: {formatDateEuropean(timeline.createdAt)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleEditTimeline}
                >
                  {t('projectTimelines.editTimeline')}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  {t('projectTimelines.deleteTimeline')}
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* View mode: General vs Detailed (cost sheets) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('projectTimelines.view')}:
            </Typography>
            <ButtonGroup size="small" variant="outlined">
              <Button
                variant={timelineViewMode === 'general' ? 'contained' : 'outlined'}
                onClick={() => setTimelineViewMode('general')}
              >
                {t('projectTimelines.generalView')}
              </Button>
              <Button
                variant={timelineViewMode === 'detailed' ? 'contained' : 'outlined'}
                onClick={() => setTimelineViewMode('detailed')}
              >
                {t('projectTimelines.detailedView')}
              </Button>
            </ButtonGroup>
            {timelineViewMode === 'detailed' && (
              <Typography variant="caption" color="text.secondary">
                {t('projectTimelines.detailedHint')}
              </Typography>
            )}
          </Box>

          {/* Scrollable timeline container: fixed width, horizontal scroll; ref for drag-to-scroll */}
          <Box
            ref={timelineScrollContainerRef}
            sx={{
              width: '100%',
              overflowX: 'auto',
              overflowY: 'visible',
              // Visible scrollbar and smooth scroll for accessibility
              scrollBehavior: 'smooth',
              '&::-webkit-scrollbar': { height: 10 },
              '&::-webkit-scrollbar-track': { bgcolor: 'action.hover' },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'action.selected', borderRadius: 1 },
              '&::-webkit-scrollbar-thumb:hover': { bgcolor: 'action.disabled' },
            }}
          >
            <GanttChart
              timelineId={timeline.id}
              viewMode={timelineViewMode}
              scrollContainerRef={timelineScrollContainerRef}
              onEditTask={(task) => {
                // Handle task editing
                console.log('Edit task:', task);
              }}
              onDeleteTask={(task) => {
                // Handle task deletion
                console.log('Delete task:', task);
              }}
              onRefresh={handleRefresh}
            />
          </Box>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            {t('projectTimelines.panHint')}
          </Typography>
        </Box>
      )}

      {/* Timeline Wizard */}
      <TimelineWizard
        open={wizardOpen}
        onClose={handleWizardClose}
        projectId={selectedProject}
        projectName={selectedProjectData?.name}
        existingTimeline={timeline}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('projectTimelines.deleteDialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('projectTimelines.deleteDialogBody', { projectName: selectedProjectData?.name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteTimeline} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectTimelines;




